import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, FileText, PlusSquare, Settings, LogOut, Package, CheckSquare, Cpu, Building2, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testId: "nav-dashboard" },
  { to: "/job-cards", icon: FileText, label: "Job Cards", testId: "nav-jobcards" },
  { to: "/job-cards/new", icon: PlusSquare, label: "New Job Card", testId: "nav-new-jc" },
];

const masterItems = [
  { to: "/customers", icon: Building2, label: "Customers" },
  { to: "/products", icon: Package, label: "Products" },
  { to: "/materials", icon: Layers, label: "Materials" },
  { to: "/machines", icon: Cpu, label: "Machines" },
  { to: "/checklist-master", icon: CheckSquare, label: "Checklist" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      <aside className="w-60 shrink-0 bg-slate-900 text-slate-100 flex flex-col no-print h-full fixed top-0 left-0 z-30">
        <div className="px-5 py-6 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img src="/brand/logo.png" alt="CTI" className="w-11 h-11 rounded bg-white p-0.5 object-contain" />
            <div>
              <div className="text-base font-semibold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>CTI Print</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Job Card System</div>
            </div>
          </Link>
        </div>

        <nav className="p-3 flex-1 space-y-1 overflow-y-auto">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}

          <div className="pt-3 pb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Masters</div>
          {masterItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}

          {user?.role === "admin" && (
            <>
              <div className="pt-3 pb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Admin</div>
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
              >
                <Settings className="w-4 h-4" />
                Settings
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="px-2 py-2 mb-2">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{user?.role}</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="w-full justify-start text-slate-200 hover:bg-slate-800 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 ml-60 h-full overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 lg:p-10">{children}</div>
      </main>
    </div>
  );
}
